import { TenantEntityTooltipIdentifierEnum } from '@app/modules/admin-tenant/components/tooltip/tenant-entity-tooltip/tenant-entity-tooltip.model';
import {
	TenantStatusEnum,
	TenantTypeEnum
}                                            from '@app/modules/admin-tenant/models/tenant.model';
import { APP_CONFIG }                        from '@appConfig/_app-config_master';
import { DeviceTypeEnum }                    from '@appConfig/media-config';
import { ITabContent }                       from '@core/interfaces/common.interfaces';
import {
	FormDataTreatmentEnum,
	IFormCreateData,
	IFormData,
	IFormUpdateData
}                                            from '@core/interfaces/form.interface';
import { TenantScopesEnum }                  from '@models/scope.enums';
import { ActionEnum }                        from '@shared/components/action-item/action-item.enum';
import {
	IStatusFlow,
	OutOfFlowDisplayModeEnum
}                                            from '@shared/components/status-flow/status-flow.model';
import { SortOrderEnum }                     from '@shared/list-operations/components/sort-filter/sort-filter.model';
import { LIST_OPERATIONS_METADATA }          from '@shared/list-operations/config/list-operations.config';
import {
	FilterIdentifiersEnum,
	FilterTypes
}                                            from '@shared/list-operations/model/filter.model';
import {
	IListOperationsConfig,
	ListOperationsIdentifierEnum
}                                            from '@shared/list-operations/model/list-operations.model';
import { PagingModeEnum }                    from '@shared/list-operations/model/paging.model';
import { RoleTypes }                         from '@shared/models/roles.model';
import { actionStatus }                      from '@shared/services/toaster.service';


export interface ITenantFormBaseData {
	additionalData: IFormData;
	archivingContractId: IFormData;
	archivingContractFrom: IFormData;
	capabilities?: IFormData;
	city: IFormData;
	countryCode: IFormData;
	customerIdentifier?: IFormData;
	detailedDescription: IFormData;
	elsterContractId: IFormData;
	elsterContractFrom: IFormData;
	email: IFormData;
	firstname: IFormData;
	fiscalContractId: IFormData;
	fiscalContractFrom: IFormData;
	id: IFormData;
	lastname: IFormData;
	name: IFormData;
	parentTenantId?: IFormData;
	phoneNumber: IFormData;
	postalCode: IFormData;
	state: IFormData;
	street: IFormData;
	houseNumber: IFormData;
	taxNumber: IFormData;
	tenantAdditionalData: IFormData;
	tenantGroup?: IFormData;
	tenantType: IFormData;
	userRoles: IFormData;
	vatId: IFormData;
	vendorCode: IFormData;
}


export interface IAdminTenantMetadata {
	entityId: string;
	entityTooltip: {
		allowedTenantIdentifiers: TenantEntityTooltipIdentifierEnum[];
		dateTimeFormat: Intl.DateTimeFormatOptions;
	};

	storeId: string;

	details: {
		dateFormat: Intl.DateTimeFormatOptions;
		internalTabContent: ITabContent[];
	};

	listOperations: IListOperationsConfig;

	i18n: {
		basepath: string;
		[ key: string ]: string;
	};

	form: {
		dateFormat: Intl.DateTimeFormatOptions;
		initiallySearchParentTenant: boolean;
		setDefaultUserRoleOnTenantTypeSwitch: boolean;
		tenantTypeToUserRolesMapByAdminType: {
			[ key: string ]: {
				[ key: string ]: {
					defaultRole: RoleTypes[]; // default roles mustn't block each other here in config
					allowedRoles: RoleTypes[];
				}
			}
		};
		baseData: ITenantFormBaseData;
		createData: IFormCreateData[];
		preserveData: IFormUpdateData[];
	};
	status: IStatusFlow<TenantStatusEnum, TenantScopesEnum>;
}


export const ADMIN_TENANT_METADATA: IAdminTenantMetadata = {
	entityId: APP_CONFIG.admin.entityId,
	entityTooltip: {
		allowedTenantIdentifiers: [
			TenantEntityTooltipIdentifierEnum.NAME,
			TenantEntityTooltipIdentifierEnum.BILLING_ADDRESS,
			TenantEntityTooltipIdentifierEnum.STATUS,
			TenantEntityTooltipIdentifierEnum.CUSTOMER_IDENTIFIER,
			TenantEntityTooltipIdentifierEnum.TENANT_TYPE,
			TenantEntityTooltipIdentifierEnum.VENDOR_CODE,
			TenantEntityTooltipIdentifierEnum.CREATED_DATE,
			TenantEntityTooltipIdentifierEnum.LAST_MODIFIED_DATE,
			TenantEntityTooltipIdentifierEnum.DESCRIPTION
		],
		dateTimeFormat: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }
	},
	storeId: APP_CONFIG.admin.store.id,
	listOperations: {
		id: ListOperationsIdentifierEnum.ACCOUNTS_LIST_PAGE,
		pagingData: {
			mode: PagingModeEnum.Light,
			continuesPagesMode: false,
			defaultValues: {
				pageSize: LIST_OPERATIONS_METADATA.default.paging.pageSize ?? 20,
				pageNumber: LIST_OPERATIONS_METADATA.default.paging.pageNumber ?? 0
			}
		},
		searchData: {
			placeholderI18nPath: 'tenant.search.placeholder'
		},
		sortData: [
			{ name: 'name', i18nPath: 'shared.forms.name.label' },
			{ name: 'type', i18nPath: 'shared.forms.type.label' },
			{ name: 'customerIdentifier', i18nPath: 'shared.forms.customerIdentifier.label' },
			{ name: 'lastModifiedDate', i18nPath: 'shared.forms.lastModifiedDate.label', defaultActive: true, sortOrder: SortOrderEnum.DESC },
			{ name: 'status', i18nPath: 'shared.forms.status.label' }
		],
		filterData: [
			{
				order: 1,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.FILTER,
				type: FilterTypes.FILTER_TEXT_TYPE,
				i18nPath: 'tenant.filter.queryFilter',
				items: [
					{ i18nPath: 'tenant.filter.queryFilter' }
				]
			},
			{
				order: 2,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.STATUS,
				type: FilterTypes.FILTER_LIST_TYPE,
				i18nPath: 'shared.forms.status',
				values: [ TenantStatusEnum.ACTIVE ],
				items: [
					{ value: TenantStatusEnum.ACTIVE, i18nPath: 'shared.status.active' },
					{ value: TenantStatusEnum.DISABLED, i18nPath: 'shared.status.disabled' }
				]
			},
			{
				order: 3,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.TYPE,
				type: FilterTypes.FILTER_OPTION_TYPE,
				i18nPath: 'tenant.filter.type',
				items: [
					{ value: TenantTypeEnum.ADMIN, i18nPath: 'shared.tenantType.admin.name' },
					{ value: TenantTypeEnum.INTERNAL, i18nPath: 'shared.tenantType.internal.name' },
					{ value: TenantTypeEnum.DISTRIBUTOR, i18nPath: 'shared.tenantType.distributor.name' },
					{ value: TenantTypeEnum.MANAGED, i18nPath: 'shared.tenantType.managed.name' },
					{ value: TenantTypeEnum.PARTNER, i18nPath: 'shared.tenantType.partner.name' },
					{ value: TenantTypeEnum.RESELLER, i18nPath: 'shared.tenantType.reseller.name' },
					{ value: TenantTypeEnum.STANDARD, i18nPath: 'shared.tenantType.standard.name' },
					{ value: TenantTypeEnum.TEST, i18nPath: 'shared.tenantType.test.name' },
					{ value: TenantTypeEnum.TRIAL, i18nPath: 'shared.tenantType.trial.name' }
				]
			}
		]
	},
	i18n: {
		basepath: APP_CONFIG.admin.i18nPath,
		page: 'page',
		entity: 'entity',
		form: 'form',
		details: 'details',
		tab: 'tab'
	},
	form: {
		dateFormat: { month: 'long', year: 'numeric' },
		initiallySearchParentTenant: false,
		setDefaultUserRoleOnTenantTypeSwitch: false,
		tenantTypeToUserRolesMapByAdminType: {
			[ RoleTypes.SUPPORT_ADMIN ]: {
				[ TenantTypeEnum.ADMIN ]: {
					defaultRole: [
						RoleTypes.CUSTOMER_ADMIN,
						RoleTypes.TENANT_ADMIN
					],
					allowedRoles: [
						RoleTypes.ACCOUNT_ADMIN,
						RoleTypes.CUSTOMER_ADMIN,
						RoleTypes.PARTNER_ADMIN,
						RoleTypes.RESELLER_ADMIN,
						RoleTypes.TAXPAYER,
						RoleTypes.TENANT_ADMIN,
						RoleTypes.VENDOR_ADMIN
					]
				},
				[ TenantTypeEnum.DISTRIBUTOR ]: {
					defaultRole: [
						RoleTypes.DISTRIBUTOR_ADMIN
					],
					allowedRoles: [
						RoleTypes.DISTRIBUTOR_ADMIN
					]
				},
				[ TenantTypeEnum.INTERNAL ]: {
					defaultRole: [
						RoleTypes.CUSTOMER_ADMIN,
						RoleTypes.TENANT_ADMIN
					],
					allowedRoles: [
						RoleTypes.ACCOUNT_ADMIN,
						RoleTypes.CUSTOMER_ADMIN,
						RoleTypes.PARTNER_ADMIN,
						RoleTypes.RESELLER_ADMIN,
						RoleTypes.TAXPAYER,
						RoleTypes.TENANT_ADMIN,
						RoleTypes.VENDOR_ADMIN
					]
				},
				[ TenantTypeEnum.MANAGED ]: {
					defaultRole: [
						RoleTypes.TENANT_ADMIN
					],
					allowedRoles: [
						RoleTypes.TENANT_ADMIN
					]
				},
				[ TenantTypeEnum.PARTNER ]: {
					defaultRole: [
						RoleTypes.CUSTOMER_ADMIN,
						RoleTypes.PARTNER_ADMIN
					],
					allowedRoles: [
						RoleTypes.CUSTOMER_ADMIN,
						RoleTypes.PARTNER_ADMIN,
						RoleTypes.TAXPAYER,
						RoleTypes.VENDOR_ADMIN
					]
				},
				[ TenantTypeEnum.RESELLER ]: {
					defaultRole: [
						RoleTypes.CUSTOMER_ADMIN,
						RoleTypes.RESELLER_ADMIN
					],
					allowedRoles: [
						RoleTypes.CUSTOMER_ADMIN,
						RoleTypes.RESELLER_ADMIN,
						RoleTypes.TAXPAYER,
					]
				},
				[ TenantTypeEnum.STANDARD ]: {
					defaultRole: [
						RoleTypes.CUSTOMER_ADMIN,
						RoleTypes.TENANT_ADMIN
					],
					allowedRoles: [
						RoleTypes.ACCOUNT_ADMIN,
						RoleTypes.CUSTOMER_ADMIN,
						RoleTypes.RESELLER_ADMIN,
						RoleTypes.TAXPAYER,
						RoleTypes.TENANT_ADMIN
					]
				},
				[ TenantTypeEnum.TEST ]: {
					defaultRole: [
						RoleTypes.ACCOUNT_ADMIN,
						RoleTypes.CUSTOMER_ADMIN
					],
					allowedRoles: [
						RoleTypes.ACCOUNT_ADMIN,
						RoleTypes.CUSTOMER_ADMIN,
						RoleTypes.TAXPAYER,
						RoleTypes.TENANT_ADMIN
					]
				},
				[ TenantTypeEnum.TRIAL ]: {
					defaultRole: [
						RoleTypes.CUSTOMER_ADMIN,
						RoleTypes.TENANT_ADMIN
					],
					allowedRoles: [
						RoleTypes.CUSTOMER_ADMIN,
						RoleTypes.TAXPAYER,
						RoleTypes.TENANT_ADMIN
					]
				}
			},
			[ RoleTypes.RESELLER_ADMIN ]: {
				[ TenantTypeEnum.PARTNER ]: {
					defaultRole: [
						RoleTypes.PARTNER_ADMIN
					],
					allowedRoles: [
						RoleTypes.PARTNER_ADMIN,
						RoleTypes.VENDOR_ADMIN
					]
				},
				[ TenantTypeEnum.STANDARD ]: {
					defaultRole: [
						RoleTypes.TENANT_ADMIN
					],
					allowedRoles: [
						RoleTypes.TENANT_ADMIN
					]
				},
				[ TenantTypeEnum.TEST ]: {
					defaultRole: [
						RoleTypes.TENANT_ADMIN
					],
					allowedRoles: [
						RoleTypes.TENANT_ADMIN
					]
				},
				[ TenantTypeEnum.MANAGED ]: {
					defaultRole: [
						RoleTypes.TENANT_ADMIN
					],
					allowedRoles: [
						RoleTypes.TENANT_ADMIN
					]
				},
				[ TenantTypeEnum.DISTRIBUTOR ]: {
					defaultRole: [
						RoleTypes.DISTRIBUTOR_ADMIN
					],
					allowedRoles: [
						RoleTypes.DISTRIBUTOR_ADMIN,
						RoleTypes.VENDOR_ADMIN
					]
				}
			},
			[ RoleTypes.DISTRIBUTOR_ADMIN ]: {
				[ TenantTypeEnum.MANAGED ]: {
					defaultRole: [
						RoleTypes.TENANT_ADMIN
					],
					allowedRoles: [
						RoleTypes.TENANT_ADMIN
					]
				},
				[ TenantTypeEnum.PARTNER ]: {
					defaultRole: [
						RoleTypes.PARTNER_ADMIN
					],
					allowedRoles: [
						RoleTypes.PARTNER_ADMIN,
						RoleTypes.VENDOR_ADMIN
					]
				},
				[ TenantTypeEnum.TEST ]: {
					defaultRole: [
						RoleTypes.TENANT_ADMIN
					],
					allowedRoles: [
						RoleTypes.TENANT_ADMIN
					]
				}
			},
			[ RoleTypes.PARTNER_ADMIN ]: {
				[ TenantTypeEnum.MANAGED ]: {
					defaultRole: [
						RoleTypes.TENANT_ADMIN
					],
					allowedRoles: [
						RoleTypes.TENANT_ADMIN
					]
				},
				[ TenantTypeEnum.TEST ]: {
					defaultRole: [
						RoleTypes.TENANT_ADMIN
					],
					allowedRoles: [
						RoleTypes.TENANT_ADMIN
					]
				}
			},
			[ RoleTypes.VENDOR_ADMIN ]: {
				[ TenantTypeEnum.MANAGED ]: {
					defaultRole: [
						RoleTypes.TENANT_ADMIN
					],
					allowedRoles: [
						RoleTypes.TENANT_ADMIN
					]
				},
				[ TenantTypeEnum.TEST ]: {
					defaultRole: [
						RoleTypes.TENANT_ADMIN
					],
					allowedRoles: [
						RoleTypes.TENANT_ADMIN
					]
				}
			}
		},
		baseData: {
			id: {
				hide: true,
				specialTreatments: null,
				order: null,
				i18nPath: ''
			},
			customerIdentifier: {
				hide: false,
				specialTreatments: null,
				order: 4,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.customerIdentifier',
				addSpaceToPrevious: false
			},
			fiscalContractId: {
				hide: false,
				specialTreatments: null,
				order: 5,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.fiscalContractId',
				addSpaceToPrevious: false

			},
			fiscalContractFrom: {
				hide: false,
				specialTreatments: null,
				order: 5,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.fiscalContractFrom',
				addSpaceToPrevious: false
			},
			archivingContractId: {
				hide: false,
				specialTreatments: null,
				order: 7,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.archivingContractId',
				addSpaceToPrevious: false
			},
			archivingContractFrom: {
				hide: false,
				specialTreatments: null,
				order: 7,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.archivingContractFrom',
				addSpaceToPrevious: false
			},
			elsterContractId: {
				hide: false,
				specialTreatments: null,
				order: 8,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.elsterContractId',
				addSpaceToPrevious: false
			},
			elsterContractFrom: {
				hide: false,
				specialTreatments: null,
				order: 8,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.elsterContractFrom',
				addSpaceToPrevious: false
			},
			parentTenantId: {
				hide: false,
				specialTreatments: null,
				order: 2,
				i18nPath: 'shared.forms.parentTenantId',
				addSpaceToPrevious: false
			},
			capabilities: {
				hide: false,
				specialTreatments: null,
				order: 6,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.capabilities',
				addSpaceToPrevious: false
			},
			tenantGroup: {
				hide: false,
				specialTreatments: null,
				order: 1,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.tenantGroup',
				addSpaceToPrevious: false
			},
			tenantType: {
				hide: false,
				specialTreatments: null,
				order: 3,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.tenantType',
				addSpaceToPrevious: false
			},
			vendorCode: {
				hide: false,
				specialTreatments: null,
				order: 10,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.vendorCode',
				addSpaceToPrevious: false
			},
			userRoles: {
				hide: false,
				specialTreatments: null,
				order: 9,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.userRoles',
				addSpaceToPrevious: false
			},
			name: {
				hide: false,
				specialTreatments: null,
				order: 15,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.name',
				addSpaceToPrevious: false
			},
			firstname: {
				hide: false,
				specialTreatments: null,
				order: 11,
				i18nPath: 'shared.forms.firstname',
				addSpaceToPrevious: false
			},
			lastname: {
				hide: false,
				specialTreatments: null,
				order: 12,
				i18nPath: 'shared.forms.lastname',
				addSpaceToPrevious: false
			},
			phoneNumber: {
				hide: true,
				specialTreatments: null,
				order: 13,
				i18nPath: 'shared.forms.phoneNumber',
				required: true,
				addSpaceToPrevious: false
			},
			email: {
				hide: false,
				specialTreatments: null,
				order: 14,
				i18nPath: 'shared.forms.email',
				required: true,
				addSpaceToPrevious: false
			},
			detailedDescription: {
				hide: true,
				specialTreatments: null,
				order: 18,
				i18nPath: 'shared.forms.detailedDescription',
				addSpaceToPrevious: false
			},
			tenantAdditionalData: {
				hide: false,
				specialTreatments: null,
				order: 19,
				i18nPath: 'shared.forms.additionalData',
				addSpaceToPrevious: true
			},
			taxNumber: {
				hide: false,
				specialTreatments: null,
				order: 16,
				i18nPath: 'shared.forms.taxNumber',
				addSpaceToPrevious: false
			},
			vatId: {
				hide: false,
				specialTreatments: null,
				order: 17,
				i18nPath: 'shared.forms.vatId',
				addSpaceToPrevious: false
			},
			street: {
				hide: false,
				specialTreatments: null,
				order: 20,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.address.street',
				addSpaceToPrevious: true
			},
			houseNumber: {
				hide: false,
				specialTreatments: null,
				order: 21,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.address.houseNumber',
				addSpaceToPrevious: true
			},
			additionalData: {
				hide: false,
				specialTreatments: null,
				order: 22,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.address.additionalData',
				addSpaceToPrevious: false
			},
			city: {
				hide: false,
				specialTreatments: null,
				order: 24,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.address.city',
				addSpaceToPrevious: false
			},
			postalCode: {
				hide: false,
				specialTreatments: null,
				order: 23,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.address.postalCode',
				addSpaceToPrevious: false
			},
			state: {
				hide: false,
				specialTreatments: null,
				order: 25,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.address.state',
				addSpaceToPrevious: false
			},
			countryCode: {
				hide: false,
				specialTreatments: [ FormDataTreatmentEnum.COUNTRY_CODE_TO_I18N ],
				order: 26,
				i18nPath: APP_CONFIG.admin.i18nPath + '.form.address.countryCode',
				addSpaceToPrevious: false
			}
		},
		createData: [],
		preserveData: [
			{ key: 'id', fallbackValue: null },
			{ key: 'customerIdentifier', fallbackValue: null },
			{ key: 'fiscalContract.contractId', fallbackValue: null },
			{ key: 'fiscalContract.from', fallbackValue: null },
			{ key: 'parentTenantId', fallbackValue: null },
			{ key: 'tenantType', fallbackValue: null },
			{ key: 'etag', fallbackValue: null },
			{ key: 'systemProperties', fallbackValue: null }
		]
	},
	details: {
		dateFormat: { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false },
		internalTabContent: [
			{
				key: 'parentTenantId',
				order: 1,
				i18nPath: 'admin.details.parentTenantId',
				addSpaceToPrevious: false,
				showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
				copyClipboard: true
			},
			{
				key: 'id',
				order: 2,
				i18nPath: 'admin.details.tenantId',
				addSpaceToPrevious: false,
				showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
				copyClipboard: true
			}
		]
	},
	status: {
		actions: [
			{
				actionItem: {
					action: ActionEnum.ACTIVATE,
					labelI18n: 'shared.buttons.activate',
					tooltipI18n: 'shared.buttons.activate',
					scopes: TenantScopesEnum.StatusEnable
				},
				dialog: {
					i18nPathButtonConfirm: 'activate',
					i18nPathDescription: 'admin.entity.dialogForActive.htmlDescription'
				},
				enforceDisableOnStatuses: [],
				enforceHideOnStatuses: [],
				order: 1,
				outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.DISABLED,
				requestActionStatus: actionStatus.ACTIVATE,
				targetStatus: TenantStatusEnum.ACTIVE
			},
			// TODO: currently not used, need clarification
			// {
			// 	actionItem: {
			// 		action: ActionEnum.SUSPEND,
			// 		actionItemIcon: ActionEnum.SUSPEND,
			// 			labelI18n: 'shared.buttons.suspend',
			// 			tooltipI18n: 'shared.buttons.suspend',
			// 		scopes: TenantScopesEnum.StatusDisable
			// 	},
			// 	dialog: {
			// 		i18nPathButtonConfirm: 'suspend',
			// 		i18nPathDescription: 'admin.entity.dialogForSuspended.htmlDescription'
			// 	},
			// 	enforceDisableOnStatuses: [],
			// 	enforceHideOnStatuses: [],
			// 	order: 2,
			// 	outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.DISABLED,
			// 	requestActionStatus: actionStatus.SUSPEND,
			// 	targetStatus: TenantStatusEnum.SUSPENDED
			// },
			{
				actionItem: {
					action: ActionEnum.DISABLE,
					labelI18n: 'shared.buttons.disable',
					tooltipI18n: 'shared.buttons.disable',
					scopes: TenantScopesEnum.StatusDisable
				},
				dialog: {
					i18nPathButtonConfirm: 'disable',
					i18nPathDescription: 'admin.entity.dialogForDisabled.htmlDescription'
				},
				enforceDisableOnStatuses: [],
				enforceHideOnStatuses: [],
				order: 3,
				outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.DISABLED,
				requestActionStatus: actionStatus.DISABLE,
				targetStatus: TenantStatusEnum.DISABLED
			}
		],
		flow: {
			[ TenantStatusEnum.ACTIVE ]: [ TenantStatusEnum.SUSPENDED, TenantStatusEnum.DISABLED ],
			[ TenantStatusEnum.SUSPENDED ]: [ TenantStatusEnum.ACTIVE, TenantStatusEnum.DISABLED ],
			[ TenantStatusEnum.DISABLED ]: [ TenantStatusEnum.ACTIVE, TenantStatusEnum.DELETED ],
			[ TenantStatusEnum.DELETED ]: []
		}
	}
};
